import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Product } from '../types';

interface ProductsCacheEntry {
  products: Product[];
  timestamp: number;
  collectionParam?: string;
  searchQuery?: string;
}

interface ProductsCacheContextType {
  getCachedProducts: (collectionParam?: string, searchQuery?: string) => Product[] | null;
  setCachedProducts: (products: Product[], collectionParam?: string, searchQuery?: string) => void;
  clearCache: () => void;
}

const ProductsCacheContext = createContext<ProductsCacheContextType | undefined>(undefined);

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const ProductsCacheProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cache, setCache] = useState<Map<string, ProductsCacheEntry>>(new Map());

  const getCacheKey = (collectionParam?: string, searchQuery?: string): string => {
    return `${collectionParam || 'all'}-${searchQuery || ''}`;
  };

  const getCachedProducts = useCallback((collectionParam?: string, searchQuery?: string): Product[] | null => {
    const key = getCacheKey(collectionParam, searchQuery);
    const entry = cache.get(key);

    if (!entry) {
      return null;
    }

    // Check if cache is still valid
    const now = Date.now();
    if (now - entry.timestamp > CACHE_DURATION) {
      // Cache expired, remove it
      setCache(prev => {
        const newCache = new Map(prev);
        newCache.delete(key);
        return newCache;
      });
      return null;
    }

    return entry.products;
  }, [cache]);

  const setCachedProducts = useCallback((products: Product[], collectionParam?: string, searchQuery?: string) => {
    const key = getCacheKey(collectionParam, searchQuery);
    setCache(prev => {
      const newCache = new Map(prev);
      newCache.set(key, {
        products,
        timestamp: Date.now(),
        collectionParam,
        searchQuery
      });
      return newCache;
    });
  }, []);

  const clearCache = useCallback(() => {
    setCache(new Map());
  }, []);

  return (
    <ProductsCacheContext.Provider value={{ getCachedProducts, setCachedProducts, clearCache }}>
      {children}
    </ProductsCacheContext.Provider>
  );
};

export const useProductsCache = (): ProductsCacheContextType => {
  const context = useContext(ProductsCacheContext);
  if (!context) {
    throw new Error('useProductsCache must be used within a ProductsCacheProvider');
  }
  return context;
};
